/**
 * Copyright ToolBelt Data Inc., 2021 All Rights Reserved
 */

import PropTypes from 'prop-types';
import {
    FormGroup,
} from 'react-bootstrap';

import NumericInput from 'react-numeric-input';
import FormRequiredText from "./form/FormRequiredText";
import FormLabel from "./form/FormLabel";
import styled from 'styled-components';

// Styled wrapper to ensure proper font-size inheritance for react-numeric-input
const NumericInputWrapper = styled.div`
    .form-control-sm {
        font-size: 0.875rem !important; /* Bootstrap's $font-size-sm */
    }
    
    .form-control-lg {
        font-size: 1.25rem !important; /* Bootstrap's $font-size-lg */
    }
`;

const FormNumericInput = (props) => {
    const {
        size,
        style,
        controlId,
        onBlur,
        onKeyDown,
        label,
        required,
        id,
        value,
        min,
        max,
        onChange,
        disabled,
        placeholder,
        step,
        isInvalid,
        errorMsg,
        height
    } = props;

    // Build className with size support and invalid state
    const className = `form-control${size === 'sm' ? ' form-control-sm' : size === 'lg' ? ' form-control-lg' : ''}${isInvalid ? ' is-invalid' : ''}`;

    const numericInputStyles = {
        input: {
            height: height || undefined,
            borderRadius: 0,
            WebkitBorderRadius: 0,
            MozBorderRadius: 0
        }
    };

    return (
        <FormGroup
            style={style}
            controlId={controlId}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        >
            {label &&
                <FormLabel className={"formTextLabel"}>
                    {label}
                    {required && (
                        <FormRequiredText> *</FormRequiredText>
                    )}
                </FormLabel>
            }

            <NumericInputWrapper>
                <NumericInput
                    className={className}
                    style={numericInputStyles}
                    id={id}
                    value={value}
                    min={min}
                    max={max}
                    onChange={(v) => {
                        // Clamp value to min/max range
                        // Convert to number if it's a string
                        let numValue = typeof v === 'string' ? parseFloat(v) : v;
                        
                        // Handle NaN or null/undefined
                        if (isNaN(numValue) || numValue === null || numValue === undefined) {
                            onChange(v); // Pass through invalid values to let validation handle them
                            return;
                        }
                        
                        // Clamp to min/max
                        let clampedValue = numValue;
                        if (min !== undefined && min !== null && clampedValue < min) {
                            clampedValue = min;
                        }
                        if (max !== undefined && max !== null && clampedValue > max) {
                            clampedValue = max;
                        }
                        onChange(clampedValue);
                    }}
                    disabled={disabled}
                    placeholder={placeholder}
                    step={step || 0.1}
                />
            </NumericInputWrapper>
            {errorMsg && (
                <span className="text-danger" style={{ fontSize: '14px' }}>
                    {errorMsg}
                </span>
            )}
        </FormGroup>
    );
};


FormNumericInput.propTypes = {
    id: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    label: PropTypes.string,
    labelStyle: PropTypes.object,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'lg']),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    isInvalid: PropTypes.bool,
    errorMsg: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    validation: PropTypes.shape({
        state: PropTypes.oneOf(['error', 'warning']),
        message: PropTypes.string,
    }),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

FormNumericInput.defaultProps = {
    style: {},
    className: '',
    labelStyle: {},
    onChange: e => e,
    onBlur: e => e,
    onKeyDown: e => e,
    isInvalid: false,
    validation: {},
    value: '',
    size: '',
    min: 0,
    max: 1,
    height: null,
};

export default FormNumericInput;
